import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
const { DateTime } = require("luxon");

import { fetch_one } from '../../../lib/v31lib';
import { loginAtom } from '../../../lib/auth';
import { User } from '../../../models/User';

import {
  Alert,
  Avatar,
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Paper,
} from '@mui/material';

import {
  Button,
  Page,
  TextInput,
  Typography
} from '../../../components/v2/styled';

/**
 * The verified eID claims object returned by POST /nl/eid/fetch.
 * Treated as a bag of optional keys; the backend has already verified
 * signature, nonce, issuer, expiry and the ACR. Never persisted.
 */
type EidClaims = {
  sub?: string;
  acr?: string;
  given_name?: string;
  middle_name?: string;
  family_name?: string;
  name?: string;
  gender?: 'male' | 'female' | string;
  birthdate?: string; // YYYY-MM-DD
  age?: number;
  place_of_birth?: string;
  address?: {
    formatted?: string;
    street_address?: string;
    locality?: string;
    postal_code?: string;
    country?: string;
  };
  photo?: string; // base64 JPEG
  beid_nationality?: string;
  beid_card_number?: string;
  beid_chip_number?: string;
  beid_document_type?: string;
  beid_card_validity_begin?: string;
  beid_card_validity_end?: string;
  beid_card_delivery_municipality?: string;
  iss?: string;
  remote_ip?: string;
  id_token_sub?: string;
};

/** A mappable eID field row. */
type FieldKey =
  | 'first_name'
  | 'last_name'
  | 'gender'
  | 'birth_date'
  | 'national_number'
  | 'street'
  | 'number'
  | 'zip'
  | 'city'
  | 'photo';

type FieldDef = {
  key: FieldKey;
  labelKey: string;
  /** card value, formatted for display */
  cardValue: string | undefined;
  /** card value in the form the save endpoint expects */
  cardRaw: string | number | undefined;
  /** current value, formatted for the hint */
  currentValue: string | undefined;
  /** grid column width */
  xs: number;
  /** whether this field has a checkbox (default true). number has none. */
  checkable?: boolean;
};

type Phase = 'explain' | 'fetching' | 'review' | 'error';

export const EidLanding: FunctionComponent = () => {
  const { t, i18n } = useTranslation(['translations']);
  const navigate = useNavigate();
  const [login, setLogin] = useAtom(loginAtom);
  const [searchParams] = useSearchParams();

  const [phase, setPhase] = useState<Phase>('explain');
  const [claims, setClaims] = useState<EidClaims | null>(null);
  const [current, setCurrent] = useState<User | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const [starting, setStarting] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [selected, setSelected] = useState<Set<FieldKey>>(new Set());
  const [streetEdit, setStreetEdit] = useState<string>('');
  const [numberEdit, setNumberEdit] = useState<string>('');

  const fetchedRef = useRef<boolean>(false);
  const targetUser = useRef<string | null>(null);
  try {
    targetUser.current = sessionStorage.getItem('eid_target_user');
  } catch (_e) {
    targetUser.current = null;
  }

  const code = searchParams.get('code');
  const eidError = searchParams.get('eid_error');
  const loc = i18n.resolvedLanguage || 'nl';

  // Phase transition: if we came back with ?code= or ?eid_error=, leave the
  // explanation screen and move into fetching / error.
  useEffect(() => {
    if (eidError) {
      setErrorMsg(eidError);
      setPhase('error');
      return;
    }
    if (code) {
      setPhase('fetching');
    }
  }, [code, eidError]);

  // Fetch claims once login is available.
  useEffect(() => {
    if (phase !== 'fetching') return;
    if (!login?.auth_token) return; // wait for rehydrate
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    fetch_one<EidClaims>(
      `/${loc}/eid/fetch`,
      { code },
      login
    )
      .then((data) => {
        setClaims(data);
        setPhase('review');
      })
      .catch((e: any) => {
        const msg = (typeof e?.data === 'string' && e.data) || 'general.eid.fetch_failed';
        setErrorMsg(msg);
        setPhase('error');
      });
  }, [phase, login, code, loc]);

  // Fetch the current user values for the hints, once login is available.
  useEffect(() => {
    if (phase !== 'review') return;
    if (!login?.auth_token) return;
    if (!targetUser.current) return;

    fetch_one<User>(
      `/${loc}/v3/objects/fetch_all`,
      {
        object: 'user',
        id: targetUser.current,
        fields: [
          'id', 'first_name', 'second_name', 'last_name', 'gender',
          'birth_date', 'national_number', 'street', 'zip', 'city', 'country_id'
        ],
        practice_id: login?.practice_id,
        only_one: 1
      },
      login
    )
      .then((data) => setCurrent(data))
      .catch(() => { /* hints are best-effort; review still works */ });
  }, [phase, login, loc]);

  // ----- helpers -----

  const genderToCode = (g?: string): number | undefined => {
    if (g === 'male') return 1;
    if (g === 'female') return 0;
    if (g === undefined || g === null) return undefined;
    return 2;
  };

  const genderLabel = (g?: string): string => {
    const code = genderToCode(g);
    if (code === 0) return t('general.genders.female');
    if (code === 1) return t('general.genders.male');
    if (code === 2) return t('general.genders.other');
    return '-';
  };

  const fmtBirthdate = (b?: string): string | undefined => {
    if (!b) return undefined;
    const d = DateTime.fromISO(b);
    return d.isValid ? d.toFormat('dd/LL/y') : b;
  };

  /** Split an e-Contract street_address (e.g. "Kerkstraat 123" or "Kerkstraat 123/3 bus 2")
   *  into street + number. Trailing number-ish token(s) go to `number`. */
  const splitStreetNumber = (s?: string): { street: string; number: string } => {
    if (!s) return { street: '', number: '' };
    // match a trailing group starting with a digit (handles "123", "123/3", "123 bus 2")
    const m = s.match(/^(.*?)(\s+\d.*)$/);
    if (m) return { street: m[1].trim(), number: m[2].trim() };
    return { street: s.trim(), number: '' };
  };

  const fields: FieldDef[] = (() => {
    if (!claims) return [];
    const c = current || ({} as User);
    const defs: FieldDef[] = [
      { key: 'first_name', labelKey: 'general.eid.read_first_name', cardValue: claims.given_name, cardRaw: claims.given_name, currentValue: c.first_name, xs: 6 },
      { key: 'last_name', labelKey: 'general.eid.read_last_name', cardValue: claims.family_name, cardRaw: claims.family_name, currentValue: c.last_name, xs: 6 },
      { key: 'gender', labelKey: 'general.eid.read_gender', cardValue: genderLabel(claims.gender), cardRaw: genderToCode(claims.gender), currentValue: [t('general.genders.female'), t('general.genders.male'), t('general.genders.other')][c.gender ?? -1], xs: 6 },
      { key: 'birth_date', labelKey: 'general.eid.read_birth_date', cardValue: fmtBirthdate(claims.birthdate), cardRaw: fmtBirthdate(claims.birthdate), currentValue: c.birth_date, xs: 6 },
      { key: 'national_number', labelKey: 'general.eid.read_card_number', cardValue: claims.beid_card_number, cardRaw: claims.beid_card_number, currentValue: c.national_number, xs: 12 },
      { key: 'street', labelKey: 'general.eid.read_street', cardValue: claims.address?.street_address, cardRaw: streetEdit, currentValue: c.street, xs: 8 },
      { key: 'number', labelKey: 'general.eid.read_number', cardValue: numberEdit, cardRaw: numberEdit, currentValue: c.number, xs: 4, checkable: false },
      { key: 'zip', labelKey: 'general.eid.read_zip', cardValue: claims.address?.postal_code, cardRaw: claims.address?.postal_code, currentValue: c.zip, xs: 4 },
      { key: 'city', labelKey: 'general.eid.read_city', cardValue: claims.address?.locality, cardRaw: claims.address?.locality, currentValue: c.city, xs: 8 },
    ];
    // photo is special: rendered as image, not text.
    if (claims.photo) {
      defs.push({ key: 'photo', labelKey: 'general.eid.read_photo', cardValue: claims.photo, cardRaw: claims.photo, currentValue: undefined, xs: 12 });
    }
    return defs;
  })();

  // Initialize the editable street/number split from the card's street_address
  // once, when claims arrive.
  useEffect(() => {
    if (claims?.address?.street_address && !streetEdit && !numberEdit) {
      const { street, number } = splitStreetNumber(claims.address.street_address);
      setStreetEdit(street);
      setNumberEdit(number);
    }
  }, [claims]);

  const cardExpired = (() => {
    if (!claims?.beid_card_validity_end) return false;
    const d = DateTime.fromISO(claims.beid_card_validity_end);
    return d.isValid && d < DateTime.now();
  })();

  const toggle = (k: FieldKey) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(fields.filter((f) => f.checkable !== false).map((f) => f.key)));
  const selectNone = () => setSelected(new Set());

  const startRead = () => {
    if (!login?.auth_token) return;
    setStarting(true);
    fetch_one<{ authorize_url: string }>(
      `/${loc}/eid/start`,
      {},
      login
    )
      .then((data) => {
        if (data?.authorize_url) {
          // Navigate full-page to e-Contract's authorize URL, SUPPRESSING the
          // Referer. e-Contract rejects a present Referer whose origin differs
          // from the redirect_uri origin (backend). An absent Referer is
          // accepted. A plain window.location.href sends a Referer and fails.
          navigateNoReferer(data.authorize_url);
        } else {
          setErrorMsg('general.eid.start_failed');
          setPhase('error');
          setStarting(false);
        }
      })
      .catch((e: any) => {
        const msg = (typeof e?.data === 'string' && e.data) || 'general.eid.start_failed';
        setErrorMsg(msg);
        setPhase('error');
        setStarting(false);
      });
  };

  const save = () => {
    if (!login?.auth_token || !targetUser.current) return;
    setSaving(true);
    const formData = new FormData();
    formData.append('object', 'user');
    formData.append('handler_id', String(login?.id || ''));
    formData.append('practice_id', String(login?.practice_id || ''));
    formData.append('fields[]', 'id');
    formData.append('ob[id]', targetUser.current);
    formData.append('id', targetUser.current);

    selected.forEach((k) => {
      const def = fields.find((f) => f.key === k);
      if (!def) return;
      if (k === 'photo') {
        // base64 -> File, same path UserEdit uses for ob[item]
        const file = b64ToFile(def.cardRaw as string, 'eid-photo.jpg');
        formData.append('ob[item]', file);
        return;
      }
      // street and number are editable; use the edited values.
      if (k === 'street') {
        formData.append('ob[street]', streetEdit || '');
        // number has no checkbox of its own; it rides along with street.
        formData.append('ob[number]', numberEdit || '');
        return;
      }
      formData.append(`ob[${k}]`, def.cardRaw === undefined ? '' : String(def.cardRaw));
    });

    fetch_one(
      `/${loc}/v3/objects/save`,
      formData,
      login
    )
      .then(() => {
        try { sessionStorage.removeItem('eid_target_user'); } catch (_e) {}
        navigate(`/user/${targetUser.current}/detail`);
      })
      .catch((e: any) => {
        const msg = (typeof e?.data === 'string' && e.data) || 'general.eid.save_failed';
        setErrorMsg(msg);
        setPhase('error');
        setSaving(false);
      });
  };

  const goBack = () => {
    try { sessionStorage.removeItem('eid_target_user'); } catch (_e) {}
    if (targetUser.current) {
      navigate(`/user/${targetUser.current}/detail`);
    } else {
      navigate('/');
    }
  };

  // ----- render -----

  return (
    <Page title={t('general.eid.page_title')}>
      <Paper sx={{ padding: 3 }}>
        {/* PHASE: explanation + start */}
        {phase === 'explain' && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5">{t('general.eid.explain_title')}</Typography>
              <Divider sx={{ marginTop: 1, marginBottom: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <Typography>{t('general.eid.explain_intro')}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography>{t('general.eid.explain_steps')}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">{t('general.eid.explain_pin')}</Alert>
            </Grid>
            {!login?.auth_token && (
              <Grid item xs={12}>
                <Alert severity="warning">{t('general.eid.waiting_auth')}</Alert>
              </Grid>
            )}
            <Grid item xs={12} sx={{ textAlign: 'right' }}>
              <Button
                id="eid_start"
                label={t('general.eid.start_cta')}
                contained
                loading={starting}
                disabled={!login?.auth_token}
                onClick={() => startRead()}
                sx={{ marginTop: 1 }}
              />
            </Grid>
          </Grid>
        )}

        {/* PHASE: fetching claims */}
        {phase === 'fetching' && (
          <Grid container spacing={2}>
            <Grid item xs={12} sx={{ textAlign: 'center' }}>
              <CircularProgress />
              <Typography sx={{ marginTop: 2 }}>{t('general.eid.fetching')}</Typography>
            </Grid>
          </Grid>
        )}

        {/* PHASE: review + select + save */}
        {phase === 'review' && claims && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5">{t('general.eid.review_title')}</Typography>
              <Divider sx={{ marginTop: 1, marginBottom: 1 }} />
            </Grid>

            {cardExpired && (
              <Grid item xs={12}>
                <Alert severity="warning">{t('general.eid.card_expired')}</Alert>
              </Grid>
            )}

            {/* select all / none */}
            <Grid item xs={12} sx={{ textAlign: 'right' }}>
              <Link component="button" underline="hover" onClick={selectAll} sx={{ marginRight: 2 }}>
                {t('general.eid.select_all')}
              </Link>
              <Link component="button" underline="hover" onClick={selectNone}>
                {t('general.eid.select_none')}
              </Link>
            </Grid>

            {fields.map((f) => {
              const isPhoto = f.key === 'photo';
              const isStreet = f.key === 'street';
              const isNumber = f.key === 'number';
              const checkable = f.checkable !== false;
              const checked = selected.has(f.key);
              return (
                <Grid item xs={f.xs} key={f.key}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    {checkable ? (
                      <Checkbox
                        sx={{ marginTop: 0.5, marginLeft: -1.5 }}
                        checked={checked}
                        onChange={() => toggle(f.key)}
                      />
                    ) : (
                      <Box sx={{ width: 34, flexShrink: 0 }} /> // aligns non-checkable number with street
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography>{t(f.labelKey)}</Typography>
                      {isPhoto ? (
                        <Avatar
                          variant="square"
                          src={`data:image/jpeg;base64,${f.cardValue}`}
                          sx={{ width: 80, height: 100, marginTop: 1 }}
                        />
                      ) : isStreet ? (
                        <TextInput
                          noCaption
                          value={streetEdit}
                          onChange={(v) => setStreetEdit(v)}
                          sx={{ marginTop: 1 }}
                        />
                      ) : isNumber ? (
                        <TextInput
                          noCaption
                          value={numberEdit}
                          onChange={(v) => setNumberEdit(v)}
                          sx={{ marginTop: 1 }}
                        />
                      ) : (
                        <Typography sx={{ fontWeight: 'bold', marginTop: 0.5 }}>
                          {f.cardValue || '-'}
                        </Typography>
                      )}
                      {!isPhoto && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                          {t('general.eid.current_value')}: {f.currentValue || '-'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              );
            })}

            <Grid item xs={12} sx={{ textAlign: 'right', marginTop: 1 }}>
              <Button
                id="eid_cancel"
                label={t('coach.general.actions.cancel')}
                onClick={() => goBack()}
                sx={{ marginRight: 1 }}
              />
              <Button
                id="eid_save"
                label={t('coach.general.actions.save')}
                contained
                loading={saving}
                disabled={selected.size === 0}
                onClick={() => save()}
              />
            </Grid>
          </Grid>
        )}

        {/* PHASE: error */}
        {phase === 'error' && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="error">{t(errorMsg || 'general.eid.error')}</Alert>
            </Grid>
            <Grid item xs={12} sx={{ textAlign: 'right' }}>
              <Button
                id="eid_error_back"
                label={t('general.eid.back')}
                onClick={() => goBack()}
              />
            </Grid>
          </Grid>
        )}
      </Paper>
    </Page>
  );
};

/** Decode a base64 (data-URI or raw) JPEG into a File, for the ob[item] save path. */
function b64ToFile(b64: string, name = 'eid-photo.jpg'): File {
  const clean = (b64 || '').replace(/^data:image\/\w+;base64,/, '');
  const bytes = Uint8Array.from(atob(clean), (c) => c.charCodeAt(0));
  return new File([bytes], name, { type: 'image/jpeg' });
}

/**
 * Full-page navigate to `url` without sending an HTTP Referer, preserving
 * the URL's query string. Uses an <a rel="noreferrer"> clicked programmatically.
 * Used for the e-Contract authorize URL: e-Contract rejects a Referer whose
 * origin differs from the redirect_uri origin, but accepts an absent one.
 *
 * Do NOT use a <form method="GET"> here — a GET form REPLACES the action
 * URL's query string with the form's own fields, stripping client_id/state/
 * etc and yielding a "missing client_id" 400 from e-Contract. The anchor
 * navigates to the exact href (query intact) while suppressing the Referer.
 */
function navigateNoReferer(url: string) {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
